import { Box, Flex, Heading, Icon, Text } from "@chakra-ui/react";
import React from "react";
import { RiMagicLine } from "react-icons/ri";
import {
  HighlightNoteEvent,
  HighlightNote as HighlightNoteEventType,
} from "./CustomEvents";
import { DocumentViewerElement } from "./DocumentViewerElement";
import { Highlight } from "./types";

type Props = {
  documentViewerRef: React.RefObject<DocumentViewerElement | undefined>;
};
export const HighlightNote = ({ documentViewerRef }: Props) => {
  const [showHighlightNote, setShowHighlightNote] = React.useState(false);
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [position, setPosition] = React.useState<
    { x: number; y: number } | undefined
  >();

  const showNote = React.useCallback(
    (args: { highlight: Highlight; x: number; y: number }) => {
      setShowHighlightNote(true);
      setTitle(args.highlight.title);
      setDescription(args.highlight.description);
      setPosition({ x: args.x, y: args.y });
    },
    [setShowHighlightNote, setTitle, setDescription]
  );
  const hideNote = React.useCallback(() => {
    setShowHighlightNote(false);
  }, []);

  const onHighlightNote = React.useCallback(
    (event: HighlightNoteEvent) => {
      switch (event.detail.action) {
        case "show": {
          showNote(event.detail);
          break;
        }
        case "hide": {
          hideNote();
        }
      }
    },
    [hideNote, showNote]
  );

  React.useEffect(() => {
    const documentViewer = documentViewerRef.current;
    if (!documentViewer) {
      return;
    }

    documentViewer.addEventListener(HighlightNoteEventType, onHighlightNote);

    return () => {
      if (!documentViewer) {
        return;
      }

      documentViewer.removeEventListener(
        HighlightNoteEventType,
        onHighlightNote
      );
    };
  }, [documentViewerRef, onHighlightNote]);

  if (!showHighlightNote) return null;

  return (
    <Flex
      position="absolute"
      left={position?.x ?? 0}
      top={(position?.y ?? 0) + 10}
      zIndex="99999"
      padding="4"
      paddingRight="6"
      bg="white"
      shadow="md"
      rounded="md"
      gap="2"
    >
      <Box>
        <Icon as={RiMagicLine} color="purple.500" boxSize="6" />
      </Box>
      <Flex direction="column" gap="2">
        <Heading as="h2" color="purple.500" fontSize="lg" fontWeight="bold">
          {title}
        </Heading>
        <Box>
          {/* Supports multiple lines and newlines*/}
          <Text whiteSpace="pre-line">{description}</Text>
        </Box>
      </Flex>
    </Flex>
  );
};
